import { createSlice } from '@reduxjs/toolkit';

import { travelPurchaseSteps } from 'constants';

const initialState = () => {
  return {
    /**
     * Categories of passengers and their respective number of passengers
     * @type {Array<{minOld: string, maxOld: string, label: string, type: string, qty: number}>}
     */
    passengersCat: [],
    /**
     * Informations about selected participants for the travel
     * @type {Array<{
     * name: string, firstName: string, birthDate: string, serialNumber: string, passengerCategory: object
     *}>}
     */
    participants: [],
    /**
     * Steps of the travel purchase process
     * @type {Array<{slug: string, order: number, label: string, current: boolean, valid: boolean, completed: boolean}>}
     */
    steps: travelPurchaseSteps,
    /**
     * Selected travel departure date in Selection Step
     * @type {string} - Format: 'YYYY-MM-DD'
     */
    departureDate: '',
    /**
     * Selected travel return date in Selection Step
     * @type {<{ label: string, value: string }>}
     */
    duration: null,
    /**
     * Selected travel departure city in Selection Step
     * @type {<{ label: string, value: string }>}
     */
    departureCity: '',
    /**
     * Selected lodging in Configuration Step
     * price is the total price for all participants and corresponds to the total price of the travel
     * price is in cents
     * @type {<{ label: string, compositionId: string, price: number, sojournId: number }>}
     */
    lodging: null,
    /**
     * Selected insurance in Insurance Step. Price is in cents
     * @type {<{ unitPrice: number, id: string, scope: Personne|Dossier }>}
     */
    insurance: null,
    /**
     * Selected subvention in Validation Step
     * @type {number|null}
     * if null, no subvention is selected. Otherwise, it is the amount of the subvention
     */
    subvention: null,
    /**
     * Selected depositChoice in Validation Step
     * @type {string|null}
     */
    depositChoice: null,
    /**
     * Selected depositAmount in Validation Step
     * @type {number|null}
     */
    depositAmount: null,
    /**
     * Selected paymentMethod in Validation Step
     * @type {string|null}
     */
    paymentMethod: null,
    /**
     * Payment head (script and css link) and body (payzen form) for the payment page with credit card
     * @type {string|null}
     */
    paymentHead: null,
    paymentBody: null,
    /**
     * Selected newsletter in Validation Step
     * @type {boolean}
     */
    newsletter: false,
    /**
     * Selected travel return date in Selection Step
     * @type {string} - Format: 'YYYY-MM-DD'
     */
    returnDate: ''
  };
};

export const travelPurchase = createSlice({
  name: 'travelPurchase',
  initialState: { ...initialState() },
  reducers: {
    navigateToPreviousTravelStep: (state, action) => {
      const currentStepIndex = state.steps.findIndex((step) => step.current);
      const previousStepIndex = state.steps.findIndex((step) => step.order === action.payload);
      state.steps[currentStepIndex].current = false;
      state.steps[previousStepIndex].current = true;
    },
    setPassengers: (state, action) => {
      state.passengersCat = action.payload || [];
    },
    setTravelPurchase: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setTravelSteps: (state, action) => {
      const { stepToUpdate, targetStep } = action.payload;
      const currentStepIndex = state.steps.findIndex((step) => step.slug === stepToUpdate.slug);
      const targetStepIndex = state.steps.findIndex((step) => step.slug === targetStep.slug);

      if (state.steps?.[currentStepIndex]) state.steps[currentStepIndex].current = false;
      if (state.steps?.[targetStepIndex]) state.steps[targetStepIndex].current = true;
    },
    updateCurrentStepValidity: (state, action) => {
      const { valid, completed } = action.payload;
      const currentStepIndex = state.steps.findIndex((step) => step.current);
      if (currentStepIndex === -1) return;
      state.steps[currentStepIndex].valid = valid;
      state.steps[currentStepIndex].completed = completed;
    },
    resetTravelPurchaseStore: (state) => {
      Object.assign(state, initialState());
    }
  }
});

// Action creators are generated for each case reducer function
export const {
  navigateToPreviousTravelStep,
  updateCurrentStepValidity,
  setTravelPurchase,
  setPassengers,
  setTravelSteps,
  resetTravelPurchaseStore
} = travelPurchase.actions;

export default travelPurchase.reducer;
